import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';

import { BreadCrumbs } from 'components/Breadcrumbs';
import { ContentLayout } from 'components/Layout';
import { DetailViewTabList } from 'components/TabList';
import { Loader } from 'components/Loader';
import { paths } from 'routes';
import { useAuth } from 'lib/auth';

import { useApplication } from '../../api/getApplication';
import { getApplicationLinks } from '../../utils/getApplicationLinks';
import { ApplicationDetailsTab } from '../../components/Applications/ApplicationDetailsTab';
import { ApplicationFilesTab } from '../../components/Applications/ApplicationFilesTab';
import { ApplicationStatusActions } from '../../components/Applications/ApplicationStatusActions';
import { ApplicationStatusChip } from '../../components/Applications/ApplicationStatusChip';

export const ApplicationDetailsView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { account, user } = useAuth();
  const tab = searchParams.get('tab') || 'details';

  const { data: application, isLoading, isError, error } = useApplication(id);

  const links = getApplicationLinks(application, { account, user });
  const fullName = application ? `${application.first_name} ${application.last_name}` : '';

  if (isError) {
    let message = error?.response?.data?.error;
    try {
      message = JSON.parse(message).error;
    } catch {}
    return <div>{message || 'Failed to load the application'}</div>;
  }

  return (
    <ContentLayout>
      {isLoading ? (
        <Loader style={{ marginTop: '40vh' }} />
      ) : (
        <Stack height="100%">
          <BreadCrumbs
            items={[
              { name: '', to: paths.applications },
              { name: 'Applications', to: paths.applications },
              { name: application?.ad_headline ?? 'Job ad', to: links.jobAd },
              { name: fullName },
            ]}
          />
          <Box
            height={50}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'white',
              paddingBottom: 1.5,
              paddingTop: 1,
            }}
          >
            <Stack pl={4}>
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Typography variant="h3">{fullName}</Typography>
                <ApplicationStatusChip status={application?.status} />
              </Stack>
              <Typography variant="caption" fontWeight={600}>
                {`${application?.company_name} (${application?.price_per_hour} SEK/h)`}
              </Typography>
            </Stack>
            {!links.isApplicant && (
              <Stack pr={4}>
                <ApplicationStatusActions application={application} />
              </Stack>
            )}
          </Box>
          <DetailViewTabList
            value={tab}
            onChange={(_, newTab) => navigate(`?tab=${newTab}`, { replace: true })}
            tabs={[{ label: 'Details' }, { label: 'Files', count: application?.files?.length ?? 0 }]}
          />
          <Stack px={4} py={2} bgcolor="background.paper" borderTop="1px solid" borderColor="border.main" height="100%">
            <ApplicationDetailsTab currentTab={tab} application={application} links={links} />
            <ApplicationFilesTab currentTab={tab} application={application} />
          </Stack>
        </Stack>
      )}
    </ContentLayout>
  );
};
